import * as THREE from "three";
import { makeHalo } from "@/components/three/maritime";

export const ROC_POS = new THREE.Vector3(-150, 0, -430);
const BEACON_Y = 15;
const N = 48;

const additive = (map, color) => new THREE.SpriteMaterial({ map, color, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });

// A low port skyline on the horizon, with the Remote Operations Center marked by a beacon.
export function buildShore(scene) {
  const trash = [];
  const keep = (o) => { trash.push(o); return o; };
  const group = new THREE.Group();
  group.position.copy(ROC_POS);
  const unit = keep(new THREE.BoxGeometry(1, 1, 1));
  const landMat = keep(new THREE.MeshBasicMaterial({ color: 0x0b1219, fog: false, transparent: true, opacity: 0 }));
  const land = new THREE.Mesh(unit, landMat);
  land.scale.set(520, 3, 60);
  land.position.y = 1.5;
  group.add(land);
  const lights = [];
  for (let i = 0; i < 34; i++) {
    const w = 6 + ((i * 37) % 17);
    const h = 2 + ((i * 53) % 11);
    const x = -250 + i * 15 + ((i * 29) % 7);
    if (Math.abs(x) < 16) continue;
    const b = new THREE.Mesh(unit, landMat);
    b.scale.set(w, h, 12);
    b.position.set(x, 3 + h / 2, 10);
    group.add(b);
    for (let k = 0; k < 3; k++) lights.push(x - w / 2 + ((k + 1) * w) / 4, 3 + ((k * 7 + i) % Math.max(h, 1)), 16.5);
  }
  const lightGeo = keep(new THREE.BufferGeometry());
  lightGeo.setAttribute("position", new THREE.Float32BufferAttribute(lights, 3));
  const lightMat = keep(new THREE.PointsMaterial({ color: 0xffc27a, size: 2, sizeAttenuation: false, transparent: true, opacity: 0, fog: false }));
  group.add(new THREE.Points(lightGeo, lightMat));

  const rocMat = keep(new THREE.MeshBasicMaterial({ color: 0x0e1822, fog: false, transparent: true, opacity: 0 }));
  const roc = new THREE.Mesh(unit, rocMat);
  roc.scale.set(24, 10, 14);
  roc.position.set(0, 8, 12);
  const stripMat = keep(new THREE.MeshBasicMaterial({ color: 0x00f2fe, fog: false, transparent: true, opacity: 0 }));
  const strip = new THREE.Mesh(unit, stripMat);
  strip.scale.set(22, 0.8, 0.4);
  strip.position.set(0, 10, 19.4);
  const halo = keep(makeHalo());
  const beaconMat = keep(additive(halo, 0x00f2fe));
  const beacon = new THREE.Sprite(beaconMat);
  beacon.position.set(0, BEACON_Y, 12);
  beacon.scale.setScalar(16);
  const beamGeo = keep(new THREE.CylinderGeometry(0.35, 0.35, 90, 8, 1, true));
  const beamMat = keep(new THREE.MeshBasicMaterial({ color: 0x00f2fe, fog: false, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
  const beam = new THREE.Mesh(beamGeo, beamMat);
  beam.position.set(0, BEACON_Y + 45, 12);
  group.add(roc, strip, beacon, beam);
  group.visible = false;
  scene.add(group);

  return {
    group,
    halo,
    beacon: new THREE.Vector3(ROC_POS.x, BEACON_Y, ROC_POS.z + 12),
    update: (k, t) => {
      group.visible = k > 0.005;
      landMat.opacity = k;
      rocMat.opacity = k;
      lightMat.opacity = k * 0.85;
      stripMat.opacity = k * (0.75 + 0.25 * Math.sin(t * 2));
      beaconMat.opacity = k;
      beamMat.opacity = k * 0.22;
      beacon.scale.setScalar(14 + Math.sin(t * 2.4) * 2.5);
    },
    dispose: () => trash.forEach((o) => o.dispose()),
  };
}

const bezier = (a, c, b, u, out) => {
  const v = 1 - u;
  return out.set(v * v * a.x + 2 * v * u * c.x + u * u * b.x, v * v * a.y + 2 * v * u * c.y + u * u * b.y, v * v * a.z + 2 * v * u * c.z + u * u * b.z);
};

// Arcs from every ship's mast to the ROC beacon, with data pulses travelling both ways.
export function buildLinks(scene, count, halo) {
  const trash = [];
  const lineMat = new THREE.LineBasicMaterial({ color: 0x00f2fe, transparent: true, opacity: 0, fog: false });
  const pulseMat = additive(halo, 0xb8fdff);
  trash.push(lineMat, pulseMat);
  const links = Array.from({ length: count }, () => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(N * 3), 3));
    const line = new THREE.Line(geo, lineMat);
    line.frustumCulled = false;
    const pulses = [new THREE.Sprite(pulseMat), new THREE.Sprite(pulseMat)];
    pulses.forEach((s) => s.scale.setScalar(3.2));
    scene.add(line, ...pulses);
    trash.push(geo);
    return { geo, line, pulses };
  });
  const ctrl = new THREE.Vector3();
  const tmp = new THREE.Vector3();

  return {
    update: (starts, end, k, t, activeCount = count) => {
      lineMat.opacity = k * 0.6;
      pulseMat.opacity = k > 0.97 ? 1 : 0;
      links.forEach((l, i) => {
        l.line.visible = i < activeCount;
        l.pulses.forEach((s) => { s.visible = i < activeCount; });
        const a = starts[i];
        ctrl.addVectors(a, end).multiplyScalar(0.5);
        ctrl.y += Math.min(70, a.distanceTo(end) * 0.18) + i * 12;
        const arr = l.geo.attributes.position.array;
        for (let j = 0; j < N; j++) {
          bezier(a, ctrl, end, j / (N - 1), tmp);
          arr[j * 3] = tmp.x;
          arr[j * 3 + 1] = tmp.y;
          arr[j * 3 + 2] = tmp.z;
        }
        l.geo.attributes.position.needsUpdate = true;
        l.geo.setDrawRange(0, Math.max(2, Math.round(N * k)));
        l.pulses.forEach((s, j) => {
          const u = (t * 0.22 + i * 0.27 + j * 0.5) % 1;
          bezier(a, ctrl, end, j ? 1 - u : u, s.position);
        });
      });
    },
    dispose: () => trash.forEach((o) => o.dispose()),
  };
}
